import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { SkeletonDetailFields } from '../../components/ui/Skeleton'
import * as audit from '../../services/storeAuditService'
import type { AdminTransactionDetail } from '../../services/storeAuditService'
import type { AuditNavState } from './storeAuditRoutes'

type Props = {
  storeId: number
  transactionRef: string
  backTo: string
  navState?: AuditNavState
}

function kindLabel(kind: string) {
  if (kind === 'ledger') return 'Ledger'
  if (kind === 'withdrawal') return 'Withdrawal'
  if (kind === 'pos_sale') return 'POS sale'
  return kind
}

function channelLabel(channel?: string) {
  if (channel === 'storefront') return 'Online orders'
  if (channel === 'in_store') return 'In-store (POS)'
  if (channel === 'wallet') return 'Wallet'
  if (channel === 'payout') return 'Payout'
  return channel || '—'
}

function formatWhen(value?: string) {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString()
}

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-sm font-medium text-foreground">{value ?? '—'}</dd>
    </div>
  )
}

function payoutSnapshotText(snapshot: unknown): string | null {
  if (snapshot == null) return null
  if (typeof snapshot === 'string') return snapshot.trim() || null
  if (typeof snapshot === 'object') {
    try {
      return JSON.stringify(snapshot, null, 2)
    } catch {
      return String(snapshot)
    }
  }
  return String(snapshot)
}

export function StoreAuditTransactionDetail({ storeId, transactionRef, backTo, navState }: Props) {
  const [detail, setDetail] = useState<AdminTransactionDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setDetail(null)
    void audit
      .getAdminStoreTransaction(storeId, transactionRef)
      .then((data) => {
        if (!cancelled) setDetail(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load transaction')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [storeId, transactionRef])

  const isPos = detail?.kind === 'pos_sale'
  const amount = detail?.kind === 'pos_sale' ? detail.total ?? detail.amount : detail?.amount
  const status =
    detail?.kind === 'ledger'
      ? detail.entryType
      : detail?.kind === 'withdrawal'
        ? detail.status
        : detail?.paymentMethod
  const payoutText = detail?.kind === 'withdrawal' ? payoutSnapshotText(detail.payoutSnapshot) : null

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link
          to={backTo}
          state={navState}
          className="inline-flex items-center justify-center rounded-2xl border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground shadow-sm transition-all duration-150 hover:bg-muted hover:shadow-md active:scale-[0.98]"
        >
          Back to list
        </Link>
        <p className="font-mono text-xs text-muted-foreground">{transactionRef}</p>
      </div>

      {error ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      {loading ? (
        <SkeletonDetailFields fields={8} />
      ) : detail ? (
        <div className="space-y-6">
          <dl className="grid gap-4 sm:grid-cols-2">
            <DetailField label="Type" value={kindLabel(detail.kind)} />
            <DetailField label="Channel" value={channelLabel(detail.salesChannel)} />
            <DetailField label="Reference" value={detail.detailRef} />
            <DetailField
              label="Amount"
              value={
                amount != null ? (
                  <span className="tabular-nums">
                    {detail.currency} {amount}
                  </span>
                ) : (
                  '—'
                )
              }
            />
            <DetailField label="Status" value={status} />
            <DetailField label="Created" value={formatWhen(detail.createdAt)} />
            {detail.kind === 'ledger' ? (
              <>
                <DetailField label="Entry type" value={detail.entryType} />
                <DetailField label="Order reference" value={detail.orderReference ?? '—'} />
                <DetailField label="Order ID" value={detail.orderId != null ? String(detail.orderId) : '—'} />
                <DetailField label="Provider" value={detail.provider ?? '—'} />
                <DetailField label="Provider reference" value={detail.providerReference ?? '—'} />
              </>
            ) : null}
            {detail.kind === 'withdrawal' ? (
              <>
                <DetailField label="Withdrawal status" value={detail.status} />
                <DetailField label="Withdrawal ID" value={String(detail.id)} />
              </>
            ) : null}
            {isPos ? (
              <>
                <DetailField label="Sale reference" value={detail.saleReference ?? '—'} />
                <DetailField label="Payment method" value={detail.paymentMethod ?? '—'} />
                {detail.subtotal != null ? (
                  <DetailField
                    label="Subtotal"
                    value={
                      <span className="tabular-nums">
                        {detail.currency} {detail.subtotal}
                      </span>
                    }
                  />
                ) : null}
                {detail.taxTotal != null ? (
                  <DetailField
                    label="Tax"
                    value={
                      <span className="tabular-nums">
                        {detail.currency} {detail.taxTotal}
                      </span>
                    }
                  />
                ) : null}
                {detail.staff ? (
                  <div className="sm:col-span-2">
                    <DetailField
                      label="Staff"
                      value={
                        <>
                          {[detail.staff.firstName, detail.staff.lastName].filter(Boolean).join(' ') || '—'}
                          {detail.staff.email ? (
                            <span className="font-normal text-muted-foreground"> · {detail.staff.email}</span>
                          ) : null}
                        </>
                      }
                    />
                  </div>
                ) : null}
              </>
            ) : null}
          </dl>

          {payoutText ? (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Payout details</h3>
              <pre className="max-h-48 overflow-auto rounded-lg border border-border bg-muted/30 p-3 text-xs whitespace-pre-wrap break-all">
                {payoutText}
              </pre>
            </div>
          ) : null}

          {isPos && Array.isArray(detail.lines) && detail.lines.length > 0 ? (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Line items</h3>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[320px] text-left text-sm">
                  <thead className="border-b border-border bg-muted/30 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">S/N</th>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Line total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.lines.map((line, rowIndex) => (
                      <tr key={line.id} className="border-b border-border/80 last:border-0">
                        <td className="px-3 py-2 tabular-nums text-muted-foreground">{rowIndex + 1}</td>
                        <td className="px-3 py-2">{line.productName || line.productSlug || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{line.quantity}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {detail.currency} {line.lineTotal}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      ) : !error ? (
        <p className="text-sm text-muted-foreground">Transaction not found.</p>
      ) : null}
    </div>
  )
}
